/**
 * Renders Primax AI form embeds (<div data-pmx-form="frm_…">) after a Barba.js page transition.
 *
 * The Primax tracker (primax.js, script[data-site-id]) scans for [data-pmx-form] once, on
 * DOMContentLoaded, and only then loads its form bundle (form-embed.js → window.PMXForms).
 * Forms that arrive with a Barba transition are never scanned, so this module:
 * 1. Destroys the previous page's form instances before the transition.
 * 2. Boots the forms in the new page, loading the form bundle first when the first page
 *    viewed had no form (the tracker never loaded it).
 */

const FORM_SELECTOR = '[data-pmx-form]';

let formBundlePromise = null;

/**
 * Loads form-embed.js from the tracker's origin. It boots itself once loaded.
 * @returns {Promise<void>}
 */
function loadFormBundle() {
    if (window.PMXForms) return Promise.resolve();
    if (formBundlePromise) return formBundlePromise;

    const trackers = document.querySelectorAll('script[data-site-id]');
    const tracker = trackers[trackers.length - 1];
    if (!tracker || !tracker.src) return Promise.reject(new Error('Primax tracker script not found'));

    formBundlePromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `${new URL(tracker.src).origin}/sdk/form-embed.js`;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => {
            formBundlePromise = null;
            reject(new Error(`failed to load ${script.src}`));
        };
        document.head.appendChild(script);
    });

    return formBundlePromise;
}

/**
 * Boots any unrendered Primax forms in the new page.
 * @param {HTMLElement} container - The new Barba container.
 * @param {boolean} debug
 */
export function initializePrimaxForms(container = document, debug = false) {
    if (!container.querySelector(FORM_SELECTOR)) return;

    if (window.PMXForms) {
        window.PMXForms.boot();
        if (debug) console.log('Primax Forms: Booted forms for new page.');
        return;
    }

    loadFormBundle()
        .then(() => {
            if (debug) console.log('Primax Forms: Loaded form bundle.');
        })
        .catch(err => {
            if (debug) console.warn('Primax Forms:', err);
        });
}

/**
 * Destroys the current page's Primax form instances before a page transition.
 */
export function destroyPrimaxForms() {
    window.PMXForms?.destroy();
}
